import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './ShopAccountSummary.styles';
import { AngleIcon } from '@/components/UI/Icons/Icons';
import { useResponsive } from '@/hooks/useResponsive';
import { formatNumber } from '@/utils/helpers';
import {
  useBusinessBalance,
  useCurrentUserPackages,
} from '@/features/Profile/core/hooks';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { BalanceSplitModal } from '@/features/Profile/core/BalanceSplitModal';
import { PendingInvoiceLink } from '@/features/Profile/core/PendingInvoiceLink';
import { ActivePackageCard } from '@/features/Profile/core/ActivePackageCard';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import {
  usePackageActivation,
  usePackagesList,
} from '@/features/Profile/sections/Packages/hooks';
import { PackageActivationModal } from '@/features/Profile/sections/Packages/components/PackageActivationModal';

interface ShopAccountSummaryProps {
  isActiveShop: boolean;
  balance: number;
  isBalanceLoading?: boolean;
  isPendingInvoice?: boolean | null;
  onAddBalance?: () => void;
}

export const ShopAccountSummary = ({
  balance,
  isActiveShop,
  isBalanceLoading,
  isPendingInvoice,
  onAddBalance,
}: ShopAccountSummaryProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const {
    balances,
    adsBalance,
    loading: isAdsBalanceLoading,
  } = useBusinessBalance();
  const { packages, loading: isPackagesLoading } = useCurrentUserPackages();
  const { packageOffers } = usePackagesList();
  const {
    close: closeActivationModal,
    openForRenewal,
    selection: activationSelection,
  } = usePackageActivation(packageOffers, packages);
  const [isBalanceSplitOpen, toggleBalanceSplit] = useToggleOpenState(false);
  const activePackage = packages[0];

  function openBalanceSplit() {
    toggleBalanceSplit(true);
  }

  function closeBalanceSplit() {
    toggleBalanceSplit(false);
  }

  function handleModalTopUp() {
    onAddBalance?.();
  }

  return (
    <>
      <S.Grid $isDesktop={isDesktop} $isActiveShop={isActiveShop}>
        {isPendingInvoice && (
          <S.PendingInvoiceBox>
            <PendingInvoiceLink isPendingInvoice={isPendingInvoice} />
          </S.PendingInvoiceBox>
        )}

        {isActiveShop && isPackagesLoading && !activePackage && (
          <S.PackageCardSlot>
            <SkeletonLoader height={150} width="100%" borderRadius={21} />
          </S.PackageCardSlot>
        )}
        {isActiveShop && activePackage && (
          <S.PackageCardSlot>
            <ActivePackageCard
              activePackage={activePackage}
              onRenew={openForRenewal}
            />
          </S.PackageCardSlot>
        )}

        <S.AccountCard $isActiveShop={isActiveShop}>
          <S.Header $isCompact={!isActiveShop}>
            <S.Label>{t('pages.cabinet.private_account')}</S.Label>
            <S.DetailsButton
              onClick={openBalanceSplit}
              aria-label={t('pages.cabinet.balance_split')}
            >
              <AngleIcon />
            </S.DetailsButton>
          </S.Header>
          {isBalanceLoading ? (
            <S.BalanceSkeleton $isCompact={!isActiveShop} />
          ) : (
            <S.Amount $isCompact={!isActiveShop}>
              {formatNumber(balance, 2)} ₼
            </S.Amount>
          )}
          <S.ActionButton $isCompact={!isActiveShop} onClick={onAddBalance}>
            {t('common.add_balance_btn')}
          </S.ActionButton>
        </S.AccountCard>

        {isActiveShop && (
          <S.AdsBalanceCard>
            <S.Header>
              <S.Label>{t('pages.cabinet.ads_balance')}</S.Label>
            </S.Header>
            {isAdsBalanceLoading ? (
              <S.BalanceSkeleton />
            ) : (
              <S.Amount>
                {adsBalance.amount === undefined
                  ? ''
                  : formatNumber(adsBalance.amount, 0)}
              </S.Amount>
            )}
            <S.Description>
              {t('pages.cabinet.remaining_ads_in_package')}
            </S.Description>
          </S.AdsBalanceCard>
        )}
      </S.Grid>

      <BalanceSplitModal
        isOpen={isBalanceSplitOpen}
        onClose={closeBalanceSplit}
        items={balances}
        onTopUp={handleModalTopUp}
      />
      {activationSelection && (
        <PackageActivationModal
          {...activationSelection}
          isOpen
          onClose={closeActivationModal}
        />
      )}
    </>
  );
};
